'use client';

// npm library imports
import Link from 'next/link';

// style import
import styles from './linkComponent.module.scss';

// type import
import type { LinkProps } from '@lib/types';
import GTMEvent from '@lib/gtm_events/GTMEvent';

const EXTERNAL_URL = /^(https?:|ftp:|mailto:|tel:)/i;

/**
 * Individual link item for link component.
 * Renders internal or external link based on URL.
 *
 * @param props - The component props object
 * @param props.text Text to display in the link
 * @param props.url URL to link to
 * @param props.onClick Optional - !ONLY WORKS WHEN PASSING FROM CLIENT COMPONENT! function to run on click
 * @param props.target Optional - _blank or _self in link. default _self
 * @param props.gtm Optional - GTM tracking config (works from server components) - { event: string, params?: Record<string, string> }
 * @param props.eventDispatch Optional - Custom event dispatch config (works from server components) - { eventName: string, eventDetail?: any }
 * @param props.ariaLabel Optional - aria-label for accessibility, should be used when link text is not descriptive enough on its own
 * @returns Individual link item
 *
 * @example
 * ```tsx
 * <LinkItem text="Link" url="/link"/>
 * ```
 */

const LinkItem = ({
    text,
    url,
    onClick,
    target = '_self',
    ariaLabel,
    gtm,
    eventDispatch,
}: LinkProps) => {

// Check if link is external or internal
    const isExternal = EXTERNAL_URL.test(url);

    // Create click handler only if needed - no useMemo overhead
    // Server components hate event handlers so this is the solution You can add any other types of similar functionality here.
    const handleClick =
    gtm || onClick || eventDispatch
        ? (e: React.MouseEvent<HTMLAnchorElement>) => {
              if (gtm) GTMEvent(gtm.event, gtm.params);
              if (eventDispatch) {
                  e.preventDefault();
                  window.dispatchEvent(
                      new CustomEvent(eventDispatch.eventName, {
                          detail: eventDispatch.eventDetail,
                      }),
                  );
              }
              onClick?.();
          }
        : undefined;

    // Render external or internal link
    return isExternal ? (
        <a
            href={url}
            onClick={handleClick}
            target={target}
            className={styles.link}
            style={{ textDecoration: 'none' }}
            aria-label={ariaLabel}
        >
            {text}
        </a>
    ) : (
        <Link
            href={url}
            onClick={handleClick}
            target={target}
            className={styles.link}
            aria-label={ariaLabel}
        >
            {text}
        </Link>
    );
};

export default LinkItem;
